import { useState, memo, useMemo, useEffect, useRef } from "react"
import { Team } from "../../types"
import { cn } from "../../lib/utils"
import { getTeamLogoUrls, getTeamColors } from "../../lib/team-utils"
import { imageCache, getImageCacheKey, isProblematicESPNUrl } from "../../lib/image-utils"
import { TeamLogoFallback } from "./team-logo-fallback"

export interface TeamLogoProps {
  team: Team
  size?: number | string
  className?: string
  lazy?: boolean
  priority?: boolean
}

function TeamLogoComponent({
  team,
  size = 40,
  className = "",
  lazy = true,
  priority = false,
}: TeamLogoProps) {
  // Memoize expensive calculations
  const { logoUrls, colors, containerStyle } = useMemo(() => {
    const { primary, secondary } = getTeamColors(team)
    const primaryColor = team.primaryColor || primary
    const secondaryColor = team.secondaryColor || secondary

    const sizeValue = typeof size === 'number' ? `${size}px` : size
    const style = {
      width: sizeValue,
      height: sizeValue,
    }

    return {
      logoUrls: getTeamLogoUrls(team),
      colors: { primaryColor, secondaryColor },
      containerStyle: style
    }
  }, [team, size])

  const { primaryColor, secondaryColor } = colors
  const [currentUrlIndex, setCurrentUrlIndex] = useState(0)
  const [useFallback, setUseFallback] = useState(false)
  const [isVisible, setIsVisible] = useState(!lazy || priority)
  const imgRef = useRef<HTMLImageElement>(null)
  const failedUrlsRef = useRef<Set<string>>(new Set())

  // Check cache for a working URL
  const cacheKey = getImageCacheKey(team.id)
  const cachedUrl = imageCache.get(cacheKey)

  // Intersection Observer for true lazy loading
  useEffect(() => {
    if (!lazy || priority || isVisible) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '50px' } // Load images 50px before they come into view
    )

    const currentRef = imgRef.current?.parentElement
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => observer.disconnect()
  }, [lazy, priority, isVisible])

  // Handle image load error by trying next URL in the list
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.currentTarget
    const currentUrl = logoUrls[currentUrlIndex]

    // CRITICAL: Prevent infinite loop by nullifying onerror
    target.onerror = null

    // Track failed URLs to prevent retry loops
    if (currentUrl) {
      failedUrlsRef.current.add(currentUrl)
    }

    // Try next URL if available and not already failed
    let nextIndex = currentUrlIndex + 1
    while (nextIndex < logoUrls.length) {
      if (!failedUrlsRef.current.has(logoUrls[nextIndex])) {
        setCurrentUrlIndex(nextIndex)
        return
      }
      nextIndex++
    }

    // All URLs failed or were previously tried, use fallback
    if (process.env.NODE_ENV === 'development') {
      console.warn(`All logo URLs failed for team: ${team.name} (${team.abbreviation})`)
      console.warn(`URLs tried:`, Array.from(failedUrlsRef.current))
    }
    setUseFallback(true)
  }

  // Handle successful image load
  const handleImageLoad = () => {
    const currentUrl = logoUrls[currentUrlIndex]
    // Cache the working URL
    if (currentUrl) {
      imageCache.set(cacheKey, currentUrl)
    }
  }

  // Use cached URL if available, otherwise start with first URL
  const currentLogoUrl = cachedUrl || logoUrls[currentUrlIndex] || logoUrls[0]

  // Reset when team changes
  useEffect(() => {
    setCurrentUrlIndex(0)
    setUseFallback(false)
    failedUrlsRef.current.clear()
  }, [team.id])

  // Skip problematic ESPN URLs and move to next
  useEffect(() => {
    if (!cachedUrl && logoUrls[currentUrlIndex] && isProblematicESPNUrl(logoUrls[currentUrlIndex])) {
      failedUrlsRef.current.add(logoUrls[currentUrlIndex])
      if (currentUrlIndex < logoUrls.length - 1) {
        setCurrentUrlIndex(prev => prev + 1)
      } else {
        setUseFallback(true)
      }
    }
  }, [currentUrlIndex, logoUrls, cachedUrl])
  
  if (useFallback) {
    return (
      <div style={containerStyle} className={cn("relative flex items-center justify-center", className)}>
        <TeamLogoFallback 
          teamName={team.name}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          className="rounded-full"
        />
      </div>
    )
  }

  // Don't render image until visible (for lazy loading)
  if (!isVisible) {
    return (
      <div
        ref={imgRef as any}
        style={containerStyle}
        className={cn("relative flex items-center justify-center bg-gray-100", className)}
      />
    )
  }

  return (
    <div style={containerStyle} className={cn("relative flex items-center justify-center", className)}>
      <img
        ref={imgRef}
        key={`${team.id}-${currentUrlIndex}`}
        src={currentLogoUrl}
        alt={`${team.name} logo`}
        className="w-full h-full object-contain"
        loading="eager" // We handle lazy loading with Intersection Observer
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  )
}

// Memoize the component to prevent unnecessary re-renders
export const TeamLogo = memo(TeamLogoComponent, (prevProps, nextProps) => {
  return (
    prevProps.team.id === nextProps.team.id &&
    prevProps.size === nextProps.size &&
    prevProps.className === nextProps.className &&
    prevProps.lazy === nextProps.lazy &&
    prevProps.priority === nextProps.priority
  )
})
